import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import { useMemo } from 'react';
import type {
  BloomfilterStatuses,
  TaskFlowHistoryOnDate,
  TaskFlowHistoryTask,
} from '../../../api/sprints-client/sprints-client.type';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { useMappedPhases } from '../../../contexts/mapped-phases.context';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting.ts';
import { resolveTaskTypeDisplayText } from '../../../helpers/task-type-labels/task-type-labels';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store.ts';
import { inkLight, secondaryBase, skyLightest } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider.tsx';
import { Text } from '../../../ui-library/typography/typography';
import { getPhasesMetadata } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.constants';
import type { Phase } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.type';
import { useWorkPeriodsTaskFlowData } from '../hooks/work-periods-calculation.hooks';
import { Metric } from '../work-periods.type.ts';

type Props = {
  selectedDate: string | null;
};

// Task card component matching Figma design
const TaskCard = ({ task }: { task: TaskFlowHistoryTask }) => {
  const i18n = useTranslation();
  const shouldTruncate = task.name.length > 30;
  const displayName = shouldTruncate ? `${task.name.slice(0, 30)}...` : task.name;

  const metadata = [
    { label: i18n.t('common.created'), value: formatDate(task.created_date, { format: 'MMM D, YYYY' }) },
    {
      label: i18n.t('common.type'),
      value: resolveTaskTypeDisplayText(task) || '-',
    },
    { label: i18n.t('common.estimation'), value: `${task?.points || '-'}` },
  ];

  return (
    <TaskCardContainer>
      <Stack gap={2}>
        <Text weight="bold" size="tiny" color={secondaryBase}>
          {task.code}
        </Text>

        <Text weight="medium" size="tiny">
          {displayName}
        </Text>

        <Divider style={{ margin: '8px 0' }} />
        <Stack gap={0}>
          {metadata.map((meta) => (
            <Group justify="space-between" key={meta.label}>
              <Text weight="medium" size="tiny" color={inkLight}>
                {meta.label}
              </Text>
              <Text weight="medium" size="tiny">
                {meta.value}
              </Text>
            </Group>
          ))}
        </Stack>
      </Stack>
    </TaskCardContainer>
  );
};

// Column header component
type ColumnHeaderProps = {
  phase: {
    id: Phase;
    label: string;
    color: string;
    description: string;
  };
  count: number;
  metric: Metric;
};

const ColumnHeader = ({ phase, count, metric }: ColumnHeaderProps) => {
  const i18n = useTranslation();
  return (
    <ColumnHeaderContainer color={`${phase.color}20`}>
      <PhaseIndicator color={phase.color} />
      <ColumnHeaderContent>
        <ColumnTitle>{phase.label}</ColumnTitle>
        <TaskCount>
          {count}{' '}
          {metric === Metric.Tasks
            ? i18n.t('common.work_units').toLowerCase()
            : i18n.t('common.estimation').toLowerCase()}
        </TaskCount>
      </ColumnHeaderContent>
    </ColumnHeaderContainer>
  );
};

export const TaskFlow = ({ selectedDate }: Props) => {
  const i18n = useTranslation();
  const { selectedMetric } = useWorkPeriodStore();
  const { data, isLoading } = useWorkPeriodsTaskFlowData();
  const { mappedPhases, resolvedLabels } = useMappedPhases();
  const allPhasesMetadata = useMemo(() => getPhasesMetadata(i18n.t, resolvedLabels), [i18n.t, resolvedLabels]);

  // Filter phases to only show those with status mappings
  const phasesMetadata = useMemo(() => {
    if (mappedPhases.length === 0) {
      return allPhasesMetadata;
    }
    return allPhasesMetadata.filter((phase) => mappedPhases.includes(phase.id));
  }, [mappedPhases, allPhasesMetadata]);

  // Get tasks for selected date
  const currentDateTasks: TaskFlowHistoryOnDate | null = useMemo(() => {
    if (!data || !selectedDate) return null;

    const formattedDate = formatDate(selectedDate, { format: 'YYYY-MM-DD' });
    return data[formattedDate] || null;
  }, [data, selectedDate]);

  return (
    <TaskFlowContainer>
      <BrandedLoadingOverlay variant="colored" visible={isLoading} scoped />

      {currentDateTasks ? (
        <ColumnsContainer>
          {phasesMetadata.map((phase) => {
            const phaseTasks = currentDateTasks[phase.id as BloomfilterStatuses] || [];

            const taskCount = phaseTasks.length;
            const pointCount = phaseTasks.reduce((acc, task) => acc + (task.points ? Number(task.points) : 0), 0);

            return (
              <Column key={phase.id}>
                <ColumnHeader
                  phase={phase}
                  count={selectedMetric === Metric.Tasks ? taskCount : pointCount}
                  metric={selectedMetric}
                />
                <TasksContainer>
                  {phaseTasks.map((task) => (
                    <TaskCard key={`${task.code}-${phase.id}`} task={task} />
                  ))}
                </TasksContainer>
              </Column>
            );
          })}
        </ColumnsContainer>
      ) : (
        <EmptyState>
          <EmptyStateText>
            {selectedDate ? 'No tasks found for selected date' : 'Please select a date to view tasks'}
          </EmptyStateText>
        </EmptyState>
      )}
    </TaskFlowContainer>
  );
};

// Styled components
const TaskFlowContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const ColumnsContainer = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  width: 100%;
  overflow: hidden;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ColumnHeaderContainer = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e3e5e5;
  border-bottom: none;
  margin-bottom: 0;
  background-color: ${(props) => props.color};
`;

const PhaseIndicator = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  flex-shrink: 0;
`;

const ColumnHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ColumnTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #000;
  line-height: 16px;
`;

const TaskCount = styled.div`
  font-family: 'Figtree', sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #6c7072;
  line-height: 14px;
`;

const TasksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  border-top: none;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const TaskCardContainer = styled.div`
  background-color: ${skyLightest};
  padding: 8px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
`;

const EmptyStateText = styled.div`
  font-family: 'Figtree', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #6c7072;
  text-align: center;
`;
